import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const TOKEN_KEYS = {
  access: "sgoffice_access_token",
  refresh: "sgoffice_refresh_token",
  user: "sgoffice_user",
};

export const AUTH_CLEARED_EVENT = "sgoffice:auth-cleared";

export const tokenStore = {
  getAccess: () => localStorage.getItem(TOKEN_KEYS.access),
  getRefresh: () => localStorage.getItem(TOKEN_KEYS.refresh),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(TOKEN_KEYS.user) || "null");
    } catch {
      return null;
    }
  },
  setSession: ({ accessToken, refreshToken, user }) => {
    if (accessToken) localStorage.setItem(TOKEN_KEYS.access, accessToken);
    if (refreshToken) localStorage.setItem(TOKEN_KEYS.refresh, refreshToken);
    if (user) localStorage.setItem(TOKEN_KEYS.user, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
    localStorage.removeItem(TOKEN_KEYS.user);
  },
};

function notifyAuthCleared() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
  }
}

export const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
    } else if (config.headers) {
      delete config.headers["Content-Type"];
    }
  }
  return config;
});

let refreshPromise = null;

function shouldSkipRefresh(config) {
  const url = config?.url || "";
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/register") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/logout")
  );
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !original ||
      original._retry ||
      shouldSkipRefresh(original)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) {
      tokenStore.clear();
      notifyAuthCleared();
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API_URL}/auth/refresh`, { refreshToken })
          .then((res) => res.data.data)
          .finally(() => {
            refreshPromise = null;
          });
      }

      const data = await refreshPromise;
      tokenStore.setSession(data);
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return api(original);
    } catch (refreshError) {
      // Only force logout when the refresh token is actually rejected
      if (refreshError.response?.status === 401) {
        tokenStore.clear();
        notifyAuthCleared();
      }
      return Promise.reject(refreshError);
    }
  }
);

export function getErrorMessage(error, fallback = "Something went wrong") {
  if (!error?.response) {
    if (error?.code === "ECONNABORTED") {
      return "Upload timed out. Try a smaller file or a faster connection.";
    }
    if (error?.message === "Network Error") {
      return "Network error — the file may be too large for the server limit, or the connection dropped.";
    }
  }
  return (
    error?.response?.data?.message ||
    error?.response?.data?.details?.confirmPassword?.[0] ||
    error?.message ||
    fallback
  );
}
